import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type {
  MasterListJournalHistory,
  MasterListJournalRequestBody,
} from '../../../types/MasterList.types';
import { getHistoryFieldLabel } from '../utils/getHistoryFieldLabel';

const StyledHistoryStatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'pending',
})<{ pending: boolean }>`
  && {
    background-color: ${(props) =>
      props.pending
        ? 'var(--color-greyish-blue)'
        : 'var(--color-light-greyish-lime-green)'};
  }
`;

interface MasterListJournalHistoryProps {
  history: MasterListJournalHistory[];
}

const getHistoryDeltaEntries = (
  delta: Partial<MasterListJournalRequestBody['delta']> | undefined
) => {
  if (!delta) return [];
  return Object.entries(delta)
    .map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? value.join(', ') : String(value ?? ''),
    }))
    .filter((entry) => entry.value.trim() !== '');
};

export function MasterListJournalHistory({
  history,
}: MasterListJournalHistoryProps) {
  const { t } = useTranslation();

  if (history.length === 0) return null;

  return (
    <Box>
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          boxShadow: 'none',
          border: 'none',
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': {
              mr: 1,
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
          }}
        >
          <Typography variant="body1" fontWeight={700}>
            {t('masterList.history')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <List disablePadding>
            {history.map((item) => (
              <ListItem key={item.id} divider disableGutters>
                <ListItemText
                  primary={
                    <Typography variant="body2" component="div" fontWeight={700}>
                      {dayjs.utc(item.updated_at).local().format('YYYY-MM-DD, HH:mm')}
                    </Typography>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <Typography variant="caption" component="div">
                        <Typography component="span">{item.updated_by}</Typography>{' '}
                        <Typography component="span" fontWeight={700}>
                          {item.action === 'deleted' ? 'ceased' : item.action}
                          {item.action !== 'deleted' ? ':' : ''}
                        </Typography>{' '}
                      </Typography>
                      {item.action !== 'deleted' &&
                        getHistoryDeltaEntries(item.delta).map((entry) => (
                          <Typography
                            key={`${item.id}-${entry.key}`}
                            variant="body1"
                            component="div"
                            sx={{
                              backgroundColor: 'var(--color-light-greyish-blue)',
                              fontWeight: 700,
                              borderRadius: 1,
                              p: 6,
                              width: 'fit-content',
                              display: 'block',
                            }}
                          >
                            {`${getHistoryFieldLabel(entry.key, t)}: ${entry.value}`}
                          </Typography>
                        ))}
                      <Typography variant="caption" component="div">
                        <Typography component="span" fontWeight={700}>
                          Change message:
                        </Typography>{' '}
                        {item.change_message}
                      </Typography>
                      <Typography variant="caption" component="div">
                        <StyledHistoryStatusChip
                          size="small"
                          pending={item.pending}
                          label={`${item.pending ? 'Pending' : 'Verified'}`}
                        />
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
